import { Group, Stack, type SelectItem } from '@mantine/core';
import type { GrantType, ResourcesIn } from '@repo/api-codegen';
import { getSummaryAndBreadCrumbs } from '@repo/common/utils/breadcrumb';
import type { MultiSelectProps } from '@repo/foundations';
import { Banner, MultiSelect, Select, Text } from '@repo/foundations';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useSecodaEntityList, type SearchResult } from '../../../api';
import { getEntityDisplayTitle } from '../../../lib/utils/entity';
import { SecodaEntityIcon } from '../../SecodaEntity';
import type { ResourceLevel } from './DataAccessRequestCreateModal';
import { mapEntityToResource } from './resourceMapper';

type ResourceSelectItem = SelectItem & {
	entity: SearchResult;
};

type ResourceSelectorItemProps = React.ComponentPropsWithoutRef<'div'> &
	ResourceSelectItem;

const ResourceSelectorItem = forwardRef<
	HTMLDivElement,
	ResourceSelectorItemProps
>(({ entity, ...others }: ResourceSelectorItemProps, ref) => {
	const breadcrumbs = getSummaryAndBreadCrumbs(
		entity.native_type || entity.entity_type,
		entity.search_metadata
	);

	return (
		<div ref={ref} {...others}>
			<Group spacing="sm" noWrap>
				<SecodaEntityIcon entity={entity} size={20} />
				<Stack spacing={0}>
					<Text size="sm" lineClamp={1} data-testid="search-result-item">
						{getEntityDisplayTitle(entity)}
					</Text>
					<Text size="xs" color="text/secondary/default" lineClamp={1}>
						{breadcrumbs}
					</Text>
				</Stack>
			</Group>
		</div>
	);
});
ResourceSelectorItem.displayName = 'ResourceSelectorItem';

export interface ResourceSelectorProps
	extends Omit<
		MultiSelectProps<ResourceSelectItem>,
		'data' | 'value' | 'setValue' | 'onChange'
	> {
	integrationId?: string;
	initialSelectedValues?: ResourcesIn[];
	accessRequestPreference?: GrantType;
	onChange: (resources: ResourcesIn[]) => void;
	onResourcesLoaded?: (count: number) => void;
	resourceLevel: ResourceLevel;
	singleSelect?: boolean;
}

export function ResourceSelector({
	integrationId,
	initialSelectedValues = [],
	onChange,
	accessRequestPreference = 'manual',
	onResourcesLoaded,
	resourceLevel,
	singleSelect = false,
	...multiSelectProps
}: ResourceSelectorProps) {
	const [searchTerm, setSearchTerm] = useState('');

	const selectedValues = useMemo(
		() => initialSelectedValues,
		[initialSelectedValues]
	);

	const selectedIds = useMemo(
		() =>
			(selectedValues?.map((item) => item.id).filter(Boolean) as string[]) ??
			[],
		[selectedValues]
	);

	const { data: selectedEntities } = useSecodaEntityList({
		filters: {
			id__in: selectedIds,
		},
		options: {
			select: (response) =>
				response.results.map(
					(item) =>
						({
							value: item.id,
							label: item.title,
							icon: <SecodaEntityIcon entity={item} size={16} />,
							entity: item,
						}) as ResourceSelectItem
				),
		},
	});

	const { data: searchResults } = useSecodaEntityList({
		filters: {
			integration_id: integrationId,
			entity_type__in: [resourceLevel],
			...(searchTerm && { title__icontains: searchTerm }),
		},
		options: {
			enabled: !!integrationId,
			select: (response) =>
				response.results.map(
					(item) =>
						({
							value: item.id,
							label: item.title,
							icon: <SecodaEntityIcon entity={item} size={16} />,
							entity: item,
						}) as ResourceSelectItem
				),
		},
	});

	const data = useMemo(
		() => [
			...(selectedEntities ?? []),
			...(searchResults ?? []).filter(
				(item) => !selectedIds.includes(item.value)
			),
		],
		[searchResults, selectedEntities, selectedIds]
	);

	const handleSetValue = useCallback(
		(values: string[]) => {
			const selectedResources = values
				.map((value) => {
					const found = data.find((item) => item.value === value);
					if (!found) return null;
					return mapEntityToResource(found.entity);
				})
				.filter(Boolean) as ResourcesIn[];
			onChange(selectedResources);
		},
		[onChange, data]
	);

	const handleSingleSelectChange = useCallback(
		(value: string | null) => {
			handleSetValue(value ? [value] : []);
		},
		[handleSetValue]
	);

	if (onResourcesLoaded && data.length > 0) {
		onResourcesLoaded(data.length);
	}

	if (singleSelect) {
		return (
			<Stack>
				<Select
					label={multiSelectProps.label}
					name={multiSelectProps.name}
					placeholder={multiSelectProps.placeholder}
					disabled={multiSelectProps.disabled}
					error={multiSelectProps.error as string | undefined}
					data={data}
					value={selectedIds[0] ?? null}
					onChange={handleSingleSelectChange}
					searchable
					searchValue={searchTerm}
					onSearchChange={setSearchTerm}
					nothingFound="No resources found"
					itemComponent={ResourceSelectorItem}
				/>
			</Stack>
		);
	}

	return (
		<Stack>
			<MultiSelect
				{...multiSelectProps}
				data={data}
				value={selectedIds}
				setValue={handleSetValue}
				searchable
				searchValue={searchTerm}
				onSearchChange={setSearchTerm}
				nothingFound="No resources found"
				itemComponent={ResourceSelectorItem}
			/>
			{resourceLevel === 'schema' && accessRequestPreference !== 'manual' && (
				<Banner
					tone="info"
					message="Selecting a schema will grant access to all existing tables within it with the selected privileges. Tables added to this schema in the future will require separate permissions."
					inCard
					aria-label="Information about schema selection indicating that all existing tables within it will be granted access with the selected privileges."
				/>
			)}
			{resourceLevel === 'database' && accessRequestPreference !== 'manual' && (
				<Banner
					tone="info"
					message="Selecting a database will grant access to all existing schemas and tables within it with the selected privileges. Schemas and tables added to this database in the future will require separate permissions."
					inCard
					aria-label="Information about database selection indicating that all existing schemas and tables within it will be granted access with the selected privileges."
				/>
			)}
		</Stack>
	);
}
